import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export default function Receivables(props) {
  let receivablesData = props.data || [];
  let onClose = props.onClose;

  // --- 1. DYNAMIC CALCULATIONS FOR SUMMARY CARDS ---
  const summary = receivablesData.reduce(
    (acc, current) => {
      acc.totalAmount += current.Total_Amount || 0;
      
      if (current.Age_0_30 > 0) { acc.age0_30.amount += current.Age_0_30; acc.age0_30.count++; }
      if (current.Age_31_60 > 0) { acc.age31_60.amount += current.Age_31_60; acc.age31_60.count++; }
      if (current.Age_61_90 > 0) { acc.age61_90.amount += current.Age_61_90; acc.age61_90.count++; }
      if (current.Age_91_120 > 0) { acc.age91_120.amount += current.Age_91_120; acc.age91_120.count++; }
      if (current.Age_Above120 > 0) { acc.ageAbove120.amount += current.Age_Above120; acc.ageAbove120.count++; }
      
      return acc;
    },
    {
      totalAmount: 0,
      age0_30: { amount: 0, count: 0 },
      age31_60: { amount: 0, count: 0 },
      age61_90: { amount: 0, count: 0 },
      age91_120: { amount: 0, count: 0 },
      ageAbove120: { amount: 0, count: 0 },
    }
  );

  const calculatedWidth = Math.max(800, receivablesData.length * 80 + 100);

  // Reusable inline styles for the summary metric cards
  const cardStyle = {
    flex: "1 1 180px",
    backgroundColor: "#ffffff",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.02)",
    minWidth: "150px"
  };

  return (
    <div
      style={{
        padding: "16px",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        margin: "0 auto",
        backgroundColor: "#f8fafc",
        color: "#334155",
        borderRadius: "16px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.03)",
      }}
    >
      {/* --- 2. SUMMARY CARDS SECTION --- */}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
        
        {/* Total outstanding card */}
        <div style={{ ...cardStyle, borderLeft: "4px solid #0f172a", backgroundColor: "#f1f5f9" }}>
          <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>Total Outstanding</div>
          <div style={{ fontSize: "22px", fontWeight: "700", color: "#0f172a", margin: "4px 0" }}>{formatCurrency(summary.totalAmount)}</div>
          <div style={{ fontSize: "11px", color: "#64748b" }}>{receivablesData.length} Customers Total</div>
        </div>

        {/* 0-30 Days */}
        <div style={cardStyle}>
          <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "500" }}>0 - 30 Days</div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#334155", margin: "4px 0" }}>{formatCurrency(summary.age0_30.amount)}</div>
          <div style={{ fontSize: "11px", color: "#94a3b8" }}>{summary.age0_30.count} active customers</div>
        </div>

        {/* 31-60 Days */}
        <div style={cardStyle}>
          <div style={{ fontSize: "12px", color: "#f97316", fontWeight: "600" }}>31 - 60 Days</div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#334155", margin: "4px 0" }}>{formatCurrency(summary.age31_60.amount)}</div>
          <div style={{ fontSize: "11px", color: "#94a3b8" }}>{summary.age31_60.count} active customers</div>
        </div>

        {/* 61-90 Days */}
        <div style={cardStyle}>
          <div style={{ fontSize: "12px", color: "#ec4899", fontWeight: "600" }}>61 - 90 Days</div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#334155", margin: "4px 0" }}>{formatCurrency(summary.age61_90.amount)}</div>
          <div style={{ fontSize: "11px", color: "#94a3b8" }}>{summary.age61_90.count} active customers</div>
        </div>

        {/* 91-120 Days */}
        <div style={cardStyle}>
          <div style={{ fontSize: "12px", color: "#ef4444", fontWeight: "600" }}>91 - 120 Days</div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#334155", margin: "4px 0" }}>{formatCurrency(summary.age91_120.amount)}</div>
          <div style={{ fontSize: "11px", color: "#94a3b8" }}>{summary.age91_120.count} active customers</div>
        </div>

        {/* Over 120 Days — Critical Warning Card */}
        <div style={{ ...cardStyle, borderLeft: "4px solid #dc2626", backgroundColor: "#fef2f2" }}>
          <div style={{ fontSize: "12px", color: "#b91c1c", fontWeight: "700", textTransform: "uppercase" }}>Over 120 Days</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: "#991b1b", margin: "4px 0" }}>{formatCurrency(summary.ageAbove120.amount)}</div>
          <div style={{ fontSize: "11px", color: "#dc2626", fontWeight: "500" }}>{summary.ageAbove120.count} Critical Accounts</div>
        </div>
      </div>

      {/* 3. DATA TABLE SECTION */}
      <div
        style={{
          overflowX: "auto",
          marginBottom: "40px",
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.02)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e2e8f0", backgroundColor: "#f1f5f9" }}>
              <th style={{ padding: "16px", fontWeight: "600", color: "#475569", fontSize: "12px" }}>Customer Name</th>
              <th style={{ padding: "16px", fontWeight: "600", color: "#475569", fontSize: "12px" }}>0 - 30 Days</th>
              <th style={{ padding: "16px", fontWeight: "600", color: "#475569", fontSize: "12px" }}>31 - 60 Days</th>
              <th style={{ padding: "16px", fontWeight: "600", color: "#475569", fontSize: "12px" }}>61 - 90 Days</th>
              <th style={{ padding: "16px", fontWeight: "600", color: "#475569", fontSize: "12px" }}>91 - 120 Days</th>
              <th style={{ padding: "16px", fontWeight: "600", color: "#b91c1c", fontSize: "12px" }}>Over 120 Days</th>
              <th style={{ padding: "16px", fontWeight: "600", color: "#0f172a", fontSize: "12px" }}>Total Amount</th>
              <th style={{ padding: "16px", fontWeight: "600", color: "#64748b", fontSize: "12px" }}>Sales Rep</th>
            </tr>
          </thead>
          <tbody>
            {receivablesData.map((row) => (
              <tr
                key={row.CustomerName}
                style={{ borderBottom: "1px solid #e2e8f0", transition: "background-color 0.2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <td style={{ padding: "16px", fontWeight: "600", color: "#0f172a", maxWidth: "240px" }}>{row.CustomerName.trim()}</td>
                <td style={{ padding: "16px", color: row.Age_0_30 > 0 ? "#334155" : "#94a3b8" }}>{formatCurrency(row.Age_0_30)}</td>
                <td style={{ padding: "16px", color: row.Age_31_60 > 0 ? "#334155" : "#94a3b8" }}>{formatCurrency(row.Age_31_60)}</td>
                <td style={{ padding: "16px", color: row.Age_61_90 > 0 ? "#334155" : "#94a3b8" }}>{formatCurrency(row.Age_61_90)}</td>
                <td style={{ padding: "16px", color: row.Age_91_120 > 0 ? "#334155" : "#94a3b8" }}>{formatCurrency(row.Age_91_120)}</td>
                <td style={{ padding: "16px", color: "#dc2626", fontWeight: row.Age_Above120 > 0 ? "600" : "400" }}>{formatCurrency(row.Age_Above120)}</td>
                <td style={{ padding: "16px", color: "#0f172a", fontWeight: "700" }}>{formatCurrency(row.Total_Amount)}</td>
                <td style={{ padding: "16px", color: "#64748b", fontSize: "13px" }}>{row.Sales_Person || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sticky Close Bar */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "flex-end",
          padding: "8px 4px",
          marginTop: "12px",
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <button
          onClick={onClose}
          className="w-full py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:opacity-95 active:scale-[0.99] transition-all tracking-wider uppercase"
        >
          Close
        </button>
      </div>
    </div>
  );
}